/**
 * ACMI 调整。考核在系统外算完，这里只记一架飞机、一段起止月和每个月的增减。
 * 入账后按航段个数平均，另加一笔 ACMI。原录入不动。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, DatePicker, Empty, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography, message } from "antd";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { settlementApi } from "../../../api/settlementApi";
import { refreshSettlementBatchPending } from "../../../utils/settlementBatchPending";

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const cardStyle = { boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" };

function errorText(error) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (detail && typeof detail.message === "string") return detail.message;
    return "操作没有完成";
}

function monthsInRange(start, end) {
    if (!start || !end || start > end) return [];
    const result = [];
    let [year, month] = start.split("-").map(Number);
    const [endYear, endMonth] = end.split("-").map(Number);
    while (year < endYear || (year === endYear && month <= endMonth)) {
        result.push(`${year}-${String(month).padStart(2, "0")}`);
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return result;
}

function money(value, signed = false) {
    if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
    const number = Number(value);
    const body = "$" + Math.abs(number).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (number < 0) return "−" + body;
    if (signed && number > 0) return "+" + body;
    return body;
}

function tone(value) {
    const number = Number(value);
    if (number > 0) return "danger";
    if (number < 0) return "success";
    return undefined;
}

function rangeLabel(start, end) {
    if (!start || !end) return "请先选择起止月";
    return `${start} 至 ${end}`;
}

const AcmiAdjustmentPage = () => {
    const [searchParams] = useSearchParams();
    const documentId = searchParams.get("document");
    const [options, setOptions] = useState({ selectable: [], unmatched: [] });
    const [aircraft, setAircraft] = useState(null);
    const [startMonth, setStartMonth] = useState(null);
    const [endMonth, setEndMonth] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [amounts, setAmounts] = useState({});
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const latestPayloadRef = useRef(null);
    const draftIdRef = useRef(null);
    const editingRef = useRef(false);
    const saveInFlightRef = useRef(false);
    const saveQueuedRef = useRef(false);
    const followUpTimerRef = useRef(null);

    const months = useMemo(() => monthsInRange(startMonth, endMonth), [startMonth, endMonth]);
    const draft = workspace?.draft || null;
    const posted = workspace?.posted || [];
    const opened = posted.find((item) => item.id === openId) || null;

    const loadWorkspace = useCallback(async (registration, start, end) => {
        if (!registration) return null;
        setLoading(true);
        try {
            const params = { aircraft: registration };
            if (start && end) {
                params.start_month = start;
                params.end_month = end;
            }
            const response = await settlementApi.acmiAdjustment.workspace(params);
            setWorkspace(response.data);
            return response.data;
        } catch (error) {
            message.error(errorText(error));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        settlementApi.acmiAdjustment
            .aircraft()
            .then((response) => {
                if (cancelled) return;
                const data = response.data || { selectable: [], unmatched: [] };
                setOptions(data);
                setAircraft((current) => current || data.selectable[0]?.registration || null);
            })
            .catch((error) => {
                if (!cancelled) message.error(errorText(error));
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!documentId) return;
        let cancelled = false;
        settlementApi.acmiAdjustment
            .get(documentId)
            .then((response) => {
                if (cancelled || !response.data) return;
                setAircraft(response.data.aircraft);
                setStartMonth(response.data.start_month);
                setEndMonth(response.data.end_month);
                setOpenId(response.data.id);
            })
            .catch((error) => {
                if (!cancelled) message.error(errorText(error));
            });
        return () => {
            cancelled = true;
        };
    }, [documentId]);

    useEffect(() => {
        if (!aircraft) return;
        loadWorkspace(aircraft, startMonth, endMonth);
    }, [aircraft, startMonth, endMonth, loadWorkspace]);

    const fillDraft = (document) => {
        const next = {};
        monthsInRange(document.start_month, document.end_month).forEach((month) => {
            next[month] = null;
        });
        (document.lines || []).forEach((line) => {
            next[line.month] = line.amount;
        });
        setStartMonth(document.start_month);
        setEndMonth(document.end_month);
        setAmounts(next);
        setReason(document.reason || "");
        setOpenId("draft");
    };

    const payloadFromForm = (document) => ({
        aircraft: document?.aircraft || aircraft,
        start_month: startMonth,
        end_month: endMonth,
        reason,
        lines: months
            .filter((month) => amounts[month] != null && amounts[month] !== "")
            .map((month) => ({ month, amount: String(amounts[month]) })),
    });

    editingRef.current = openId === "draft" && Boolean(draft?.id) && !saving && Boolean(startMonth && endMonth);
    if (editingRef.current) {
        draftIdRef.current = draft.id;
        latestPayloadRef.current = payloadFromForm(draft);
    }

    const flushDraftSave = useCallback(() => {
        if (!editingRef.current || !draftIdRef.current || !latestPayloadRef.current) return;
        if (saveInFlightRef.current) {
            saveQueuedRef.current = true;
            return;
        }
        const payload = latestPayloadRef.current;
        const draftId = draftIdRef.current;
        saveInFlightRef.current = true;
        saveQueuedRef.current = false;
        settlementApi.acmiAdjustment
            .update(draftId, payload)
            .catch((error) => {
                const text = errorText(error);
                if (text.includes("已经入账")) return;
                message.error(text);
            })
            .finally(() => {
                saveInFlightRef.current = false;
                if (!saveQueuedRef.current || !editingRef.current) {
                    saveQueuedRef.current = false;
                    return;
                }
                saveQueuedRef.current = false;
                if (followUpTimerRef.current) clearTimeout(followUpTimerRef.current);
                followUpTimerRef.current = setTimeout(() => {
                    followUpTimerRef.current = null;
                    flushDraftSave();
                }, 600);
            });
    }, []);

    useEffect(() => {
        if (!editingRef.current) {
            saveQueuedRef.current = false;
            if (followUpTimerRef.current) {
                clearTimeout(followUpTimerRef.current);
                followUpTimerRef.current = null;
            }
            return undefined;
        }
        const timer = setTimeout(() => {
            flushDraftSave();
        }, 600);
        return () => clearTimeout(timer);
    }, [openId, draft, amounts, reason, startMonth, endMonth, months, aircraft, saving, flushDraftSave]);

    useEffect(() => {
        return () => {
            if (followUpTimerRef.current) clearTimeout(followUpTimerRef.current);
        };
    }, []);

    const startDraft = async () => {
        if (!startMonth || !endMonth) {
            message.warning("请先选择起止月。");
            return;
        }
        if (draft && draft.aircraft === aircraft) {
            fillDraft(draft);
            return;
        }
        setSaving(true);
        try {
            const response = await settlementApi.acmiAdjustment.create({
                aircraft,
                start_month: startMonth,
                end_month: endMonth,
                reason: "",
                lines: [],
            });
            fillDraft(response.data);
            await loadWorkspace(aircraft, startMonth, endMonth);
            setOpenId("draft");
        } catch (error) {
            message.error(errorText(error));
        } finally {
            setSaving(false);
        }
    };

    const askPost = () => {
        if (!draft) {
            message.warning("还没有草稿。");
            return;
        }
        if (!startMonth || !endMonth) {
            message.warning("请先选择起止月。");
            return;
        }
        if (!reason.trim()) {
            message.warning("先写说明，以后才知道为什么调。");
            return;
        }
        if (months.some((month) => amounts[month] == null || amounts[month] === "")) {
            message.warning("这段里每个月都要填金额。这个月不动就填 0。");
            return;
        }
        if (months.every((month) => Number(amounts[month]) === 0)) {
            message.warning("至少有一个月要写出增减。");
            return;
        }
        Modal.confirm({
            title: "入账后不能改这张单据的数字",
            content: "写错了可以撤回，也可以再新建一张，金额加在已有调整上。原录入不动。",
            okText: "确认入账",
            cancelText: "返回修改",
            onOk: async () => {
                setSaving(true);
                try {
                    const response = await settlementApi.acmiAdjustment.post(draft.id, {
                        ...payloadFromForm(draft),
                        lines: months.map((month) => ({ month, amount: String(amounts[month]) })),
                    });
                    refreshSettlementBatchPending(response.data?.affected_batch_ids);
                    message.success(response.data?.message || "已入账");
                    setOpenId(response.data.id);
                    await loadWorkspace(aircraft, startMonth, endMonth);
                } catch (error) {
                    message.error(errorText(error));
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    const askWithdraw = (document) => {
        Modal.confirm({
            title: "撤回这张单据？",
            content: "撤回后回到草稿。航段上这张单据的钱不再计入。入账人和说明还留着。",
            okText: "确认撤回",
            cancelText: "留下",
            okButtonProps: { danger: true },
            onOk: async () => {
                setSaving(true);
                try {
                    const response = await settlementApi.acmiAdjustment.withdraw(document.id);
                    refreshSettlementBatchPending(response.data?.affected_batch_ids);
                    message.success(response.data?.message || "已撤回");
                    setStartMonth(document.start_month);
                    setEndMonth(document.end_month);
                    fillDraft(response.data);
                    await loadWorkspace(document.aircraft, document.start_month, document.end_month);
                } catch (error) {
                    message.error(errorText(error));
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    const abandonDraft = async () => {
        if (!draft) return;
        setSaving(true);
        try {
            const response = await settlementApi.acmiAdjustment.abandon(draft.id);
            message.success(response.data?.message || "已放弃这张草稿");
            setOpenId(null);
            setAmounts({});
            setReason("");
            await loadWorkspace(aircraft, startMonth, endMonth);
        } catch (error) {
            message.error(errorText(error));
        } finally {
            setSaving(false);
        }
    };

    const openPosted = (document) => {
        setAircraft(document.aircraft);
        setStartMonth(document.start_month);
        setEndMonth(document.end_month);
        setOpenId(document.id);
    };

    const pictures = workspace?.months || [];
    const pictureOf = (month) => pictures.find((item) => item.month === month);

    const monthCards = (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {months.map((month) => {
                const picture = pictureOf(month);
                const actual = picture ? Number(picture.original_total) + Number(picture.posted_adjustment_total) : null;
                return (
                    <span key={month} style={{ display: "inline-flex", alignItems: "baseline", gap: 8, padding: "2px 10px", background: "#fff", border: "1px solid #f0f0f0", borderRadius: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {month} 实际
                        </Text>
                        <Text strong type={tone(actual)}>
                            {actual == null ? "—" : money(actual)}
                        </Text>
                    </span>
                );
            })}
        </div>
    );

    const aircraftSelect = (
        <Select
            value={aircraft}
            style={{ width: "100%" }}
            placeholder="选择飞机"
            onChange={(value) => {
                setAircraft(value);
                setOpenId(null);
            }}
            options={(options.selectable || []).map((item) => ({ value: item.registration, label: item.registration }))}
        />
    );

    const slipMenu = (
        <Card title="这段考核" style={cardStyle}>
            <div style={{ marginBottom: 8 }}>机号</div>
            {aircraftSelect}
            <div style={{ margin: "16px 0 8px" }}>起止月</div>
            <RangePicker
                picker="month"
                allowClear
                style={{ width: "100%" }}
                placeholder={["起始月", "结束月"]}
                value={startMonth && endMonth ? [dayjs(startMonth), dayjs(endMonth)] : null}
                onChange={(dates) => {
                    if (!dates || !dates[0] || !dates[1]) {
                        setStartMonth(null);
                        setEndMonth(null);
                        return;
                    }
                    const left = dates[0].format("YYYY-MM");
                    const right = dates[1].format("YYYY-MM");
                    setStartMonth(left < right ? left : right);
                    setEndMonth(left < right ? right : left);
                }}
            />
            <div style={{ margin: "16px 0 8px" }}>已入账的单据</div>
            {posted.length ? (
                posted.map((item) => (
                    <Button
                        key={item.id}
                        block
                        style={{ marginBottom: 8, textAlign: "left", height: "auto" }}
                        type={openId === item.id ? "primary" : "default"}
                        onClick={() => openPosted(item)}
                    >
                        <div>{rangeLabel(item.start_month, item.end_month)}</div>
                        <Text type={openId === item.id ? undefined : "secondary"}>
                            {item.posted_on || "—"} · {item.posted_by || "—"} · {money(item.net_amount, true)}
                        </Text>
                    </Button>
                ))
            ) : (
                <Text type="secondary">还没有入账</Text>
            )}
            {draft ? (
                <Button block style={{ marginTop: 8 }} type={openId === "draft" ? "primary" : "default"} onClick={() => fillDraft(draft)}>
                    回到草稿
                </Button>
            ) : (
                <Button type="primary" block style={{ marginTop: 16 }} loading={saving} disabled={!aircraft || !startMonth || !endMonth} onClick={startDraft}>
                    新建调整
                </Button>
            )}
        </Card>
    );

    const draftView = draft ? (
        <Card style={cardStyle} title={rangeLabel(draft.start_month, draft.end_month)} extra={<Tag color="processing">草稿</Tag>}>
            <Table
                size="middle"
                pagination={false}
                rowKey="month"
                dataSource={months.map((month) => ({ month, picture: pictureOf(month) }))}
                columns={[
                    { title: "月份", dataIndex: "month" },
                    {
                        title: "原录入",
                        align: "right",
                        render: (_, row) => money(row.picture?.original_total),
                    },
                    {
                        title: "已入账调整",
                        align: "right",
                        render: (_, row) => (Number(row.picture?.posted_adjustment_total) ? <Text type={tone(row.picture.posted_adjustment_total)}>{money(row.picture.posted_adjustment_total, true)}</Text> : "—"),
                    },
                    {
                        title: "这张的增减",
                        width: 180,
                        render: (_, row) => (
                            <InputNumber
                                style={{ width: "100%" }}
                                stringMode
                                value={amounts[row.month]}
                                onChange={(value) => setAmounts((current) => ({ ...current, [row.month]: value }))}
                            />
                        ),
                    },
                    {
                        title: "可分航段",
                        align: "right",
                        render: (_, row) => row.picture?.eligible_count ?? "—",
                    },
                ]}
            />
            {pictures.some((item) => item.problem) ? (
                <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message={pictures
                        .map((item) => item.problem)
                        .filter(Boolean)
                        .join("；")}
                />
            ) : null}
            <div style={{ margin: "16px 0 8px" }}>说明</div>
            <Input.TextArea rows={4} value={reason} placeholder="写清楚哪几个月、为什么加、为什么减。" onChange={(event) => setReason(event.target.value)} />
            <Space style={{ marginTop: 16 }}>
                <Button type="primary" loading={saving} onClick={askPost}>
                    入账
                </Button>
                <Button loading={saving} onClick={abandonDraft}>
                    放弃草稿
                </Button>
            </Space>
        </Card>
    ) : null;

    const postedView = opened ? (
        <Card
            style={cardStyle}
            title={rangeLabel(opened.start_month, opened.end_month)}
            extra={<Tag color="success">已入账 · {opened.posted_on} · {opened.posted_by || "—"}</Tag>}
        >
            <Table
                size="middle"
                pagination={false}
                rowKey="month"
                dataSource={opened.lines || []}
                columns={[
                    { title: "月份", dataIndex: "month" },
                    {
                        title: "这张单据",
                        dataIndex: "amount",
                        align: "right",
                        render: (value) => <Text type={tone(value)}>{money(value, true)}</Text>,
                    },
                    {
                        title: "其他调整",
                        align: "right",
                        render: (_, row) => {
                            const picture = pictureOf(row.month);
                            const others = picture ? Number(picture.posted_adjustment_total) - Number(row.amount) : null;
                            return others ? <Text type={tone(others)}>{money(others, true)}</Text> : "—";
                        },
                    },
                ]}
            />
            <Paragraph style={{ marginTop: 16, marginBottom: 0, whiteSpace: "pre-wrap" }}>{opened.reason}</Paragraph>
            {(opened.lines || []).some((line) => (line.legs || []).length) ? (
                <Table
                    style={{ marginTop: 16 }}
                    size="small"
                    pagination={false}
                    rowKey={(row) => `${row.month}-${row.flight_operation_id}`}
                    dataSource={(opened.lines || []).flatMap((line) => (line.legs || []).map((leg) => ({ ...leg, month: line.month })))}
                    columns={[
                        { title: "月份", dataIndex: "month" },
                        { title: "航班号", dataIndex: "flight_number" },
                        { title: "航段", render: (_, row) => `${row.departure_airport}-${row.arrival_airport}` },
                        { title: "起飞日", dataIndex: "departure_date" },
                        {
                            title: "分到",
                            dataIndex: "amount",
                            align: "right",
                            render: (value, row) => (
                                <Space>
                                    <Text type={tone(value)}>{money(value, true)}</Text>
                                    {row.got_remainder ? <Tag>含零头</Tag> : null}
                                </Space>
                            ),
                        },
                    ]}
                />
            ) : null}
            <Space style={{ marginTop: 16 }}>
                <Button danger loading={saving} onClick={() => askWithdraw(opened)}>
                    撤回这张
                </Button>
            </Space>
        </Card>
    ) : null;

    const rangePreview = startMonth && endMonth ? (
        <Card style={cardStyle} title={rangeLabel(startMonth, endMonth)}>
            <Table
                size="middle"
                pagination={false}
                rowKey="month"
                dataSource={pictures}
                columns={[
                    { title: "月份", dataIndex: "month" },
                    { title: "原录入", dataIndex: "original_total", align: "right", render: (value) => money(value) },
                    {
                        title: "已入账调整",
                        dataIndex: "posted_adjustment_total",
                        align: "right",
                        render: (value) => (Number(value) ? <Text type={tone(value)}>{money(value, true)}</Text> : "—"),
                    },
                    { title: "可分航段", dataIndex: "eligible_count", align: "right" },
                ]}
            />
            <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                这段还没有正在写的草稿。请用左侧的「新建调整」。增减会落在起止月之间的每一个月，入账后按航段个数平均。
            </Paragraph>
        </Card>
    ) : (
        <Card style={cardStyle}>
            <Empty description="请先选择起止月" />
        </Card>
    );

    let body = rangePreview;
    if (openId === "draft" && draft) body = draftView;
    else if (opened) body = postedView;

    return (
        <div style={{ padding: 16 }}>
            <Card style={{ ...cardStyle, marginBottom: 16 }}>
                <Title level={4} style={{ marginTop: 0 }}>
                    ACMI 调整
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    考核在系统外算完。按一架飞机选起止月，每个月写一个增减。入账后，这个月的增减只平均分给已经有 ACMI 原录入、并且会出现在航段费用该月列表里的航段，各多记一笔。余数记在该月排在最后的那段上。原录入不动。
                </Paragraph>
            </Card>
            {(options.unmatched || []).length ? (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    message="以下机号未在飞机主数据中登记，无法选择"
                    description={(options.unmatched || []).slice(0, 8).map((item) => item.registration).join("、")}
                />
            ) : null}
            {!aircraft && !loading ? <Empty description="当前组织里还没有能对上主数据的飞机" /> : null}
            {aircraft ? (
                <>
                    {monthCards}
                    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "start" }}>
                        {slipMenu}
                        <div>{body}</div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default AcmiAdjustmentPage;
